import React, { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { apiService } from '../services/api'
import { money, fmtDate, nowLocalInput } from '../lib/ui'
import StoreAutocomplete from '../components/StoreAutocomplete'

const AddReceiptPage: React.FC = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const camRef = useRef<HTMLInputElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const [company, setCompany] = useState('')
  const [amount, setAmount] = useState('')
  const [datetime, setDatetime] = useState(nowLocalInput())
  const [payer, setPayer] = useState<'David' | 'Timna'>('David')
  const [isShared, setIsShared] = useState(true)
  const [notes, setNotes] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState('')

  const { data: storesData } = useQuery({ queryKey: ['stores'], queryFn: apiService.getStores })
  const stores = storesData?.stores || []

  const selectedDate = datetime ? datetime.slice(0, 10) : ''
  const { data: sameDay = [] } = useQuery({
    queryKey: ['receipts', 'same-day', selectedDate],
    queryFn: () => apiService.getReceipts({ start_date: selectedDate + 'T00:00:00', end_date: selectedDate + 'T23:59:59' }),
    enabled: !!selectedDate,
  })

  const processMutation = useMutation({
    mutationFn: apiService.processReceipt,
    onSuccess: (data) => {
      setCompany(data.company)
      setAmount(String(data.total_amount))
      setDatetime(data.datetime.length > 16 ? data.datetime.slice(0, 16) : data.datetime)
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Could not read the photo'),
  })

  const createMutation = useMutation({
    mutationFn: () =>
      apiService.createReceipt(
        { company, total_amount: parseFloat(amount) || 0, datetime, payer, is_shared: isShared, notes },
        file || undefined,
      ),
    onSuccess: () => {
      toast.success('Receipt saved')
      queryClient.invalidateQueries({ queryKey: ['receipts'] })
      queryClient.invalidateQueries({ queryKey: ['summary'] })
      queryClient.invalidateQueries({ queryKey: ['monthly-report'] })
      queryClient.invalidateQueries({ queryKey: ['stores'] })
      queryClient.invalidateQueries({ queryKey: ['available-years'] })
      navigate('/search')
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Failed to save receipt'),
  })

  const onPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (!f.type.startsWith('image/')) { toast.error('Please choose an image'); return }
    setFile(f)
    setFileName(f.name)
    processMutation.mutate(f)
  }

  const save = () => {
    if (!company || !amount) { toast.error('Enter a store and amount'); return }
    createMutation.mutate()
  }

  return (
    <>
      <h2 className="page">Add receipt</h2>
      <p className="sub">One form for everything. Attach a photo to auto-fill it, or just type the details.</p>

      <div className="glass-card">
        <div className="tiny muted" style={{ marginBottom: 8 }}>
          Add a photo <span className="muted">(optional — we'll auto-fill the form from it)</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" className="photo-btn" onClick={() => camRef.current?.click()}>Take photo<small>use your camera</small></button>
          <button type="button" className="photo-btn" onClick={() => fileRef.current?.click()}>Choose file<small>from your device</small></button>
        </div>
        <div className="tiny muted" style={{ marginTop: 8, minHeight: 16 }}>{fileName ? 'Attached: ' + fileName : ''}</div>
        <input ref={camRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={onPhoto} />
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPhoto} />

        {processMutation.isLoading && <div className="note note-info">Extracting…</div>}
        {processMutation.isSuccess && !processMutation.isLoading && (
          <div className="note note-ok">Auto-filled from photo — review &amp; correct anything below.</div>
        )}

        <div className="field">
          <label>Store / company</label>
          <StoreAutocomplete value={company} onChange={setCompany} options={stores} placeholder="e.g. Billa" />
        </div>
        <div className="field"><label>Amount (€)</label><input type="number" step="0.01" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
        <div className="field"><label>Date &amp; time</label><input type="datetime-local" value={datetime} onChange={(e) => setDatetime(e.target.value)} /></div>
        <div className="field">
          <label>Who paid?</label>
          <div className="radios">
            <div className={`radio${payer === 'David' ? ' on' : ''}`} onClick={() => setPayer('David')}>D · David</div>
            <div className={`radio${payer === 'Timna' ? ' on' : ''}`} onClick={() => setPayer('Timna')}>T · Timna</div>
          </div>
        </div>
        <div className="field">
          <label>Split type</label>
          <div className="switch">
            <div>
              <div style={{ fontWeight: 600 }}>{isShared ? 'Shared 50/50' : 'Personal'}</div>
              <div className="tiny muted">{isShared ? "toggle off if it's personal (other owes full)" : 'other person owes the full amount'}</div>
            </div>
            <div className={`toggle${isShared ? ' on' : ''}`} onClick={() => setIsShared(!isShared)} />
          </div>
        </div>
        <div className="field"><label>Notes (optional)</label><textarea placeholder="anything worth remembering…" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>

        {selectedDate && sameDay.length > 0 && (
          <div className="note note-warn" style={{ marginTop: 12 }}>
            <b>{sameDay.length} receipt{sameDay.length > 1 ? 's' : ''}</b> already on {fmtDate(selectedDate)} — check you're not duplicating:
            <br />
            {sameDay.map((r) => `• ${r.company} ${money(r.total_amount)} (${r.payer})`).join('  ')}
          </div>
        )}

        <button className="cta" style={{ width: '100%', justifyContent: 'center', marginTop: 18 }} disabled={createMutation.isLoading} onClick={save}>
          {createMutation.isLoading ? 'Saving…' : 'Save receipt'}
        </button>
      </div>
    </>
  )
}

export default AddReceiptPage
